import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BidItem, BidSheet, Heading, ReportBidItem, Site } from '../models';
import { AppHttpService } from './app-http.service';
import { saveAs } from 'file-saver';
import { Util } from '../helpers';
import { Parser } from 'expr-eval';

@Injectable({ providedIn: 'root' })
export class BidService {

  constructor(
    private appHttpService: AppHttpService
  ) {}

  getBidSheet(projectId: string, qp = {}) {
    return this.appHttpService.getService(`/v1/bid/${projectId}/sheet`, qp).pipe(
      map((resp: any) => {
        if (resp.data) {
          return new BidSheet(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Deletes bidItem
   * @param bidItem BidItem
   */
  delete(bidItem: BidItem) {
    // Todo check acl here
    return this.appHttpService.deleteService(`/v1/bid/item/${bidItem.id}`);
  }

  /**
   * Create a new Bid Item
   * @param payload object
   */
  createBidItem(payload: any, qp: any = {}) {
    return this.appHttpService.postService(`/v1/bid/sheet/${payload.bid_sheet_id}/item`, payload, qp).pipe(
      map((resp: any) => {
        if (resp.data) {
          return new BidItem(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Update Bid Item
   * @param id string
   * @param payload object
   */
  updateBidItem(id, payload: any, qp: any = {}) {
    return this.appHttpService.putService(`/v1/bid/item/${id}`, payload, qp).pipe(
      map((resp: any) => {
        if (resp.data) {
          return new BidItem(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  bulkUpdateTrackMaterialQuantity(payload: { ids?: string[]; project_id?: string; all?: boolean; track_material_quantity: boolean }): Observable<{ updated_count: number }> {
    return this.appHttpService.patchService('/v1/bid/item/bulk', payload).pipe(
      map((resp: any) => resp.data)
    );
  }

  /**
   * Create new bidsheet for project
   * @param project Project
   */
  createSheet(project) {
    const body = { name: `bid sheet` };
    return this.appHttpService.postService(`/v1/bid/${project.id}/sheet`, body).pipe(
      map((resp: any) => {
        if (resp.data) {
          return new BidSheet(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Fetches bid items
   * @param bidSheet BidSheet
   * @param pagination Pagination
   * @param qp query-params
   * @returns {Promise<any>}
   */
  getBidItems(bidSheet: BidSheet, pagination: any = {}, qp: any = {}): Promise<any> {
    qp = Object.assign({
      start: (pagination.pageIndex ?? 0) * (pagination.pageSize ?? 10),
      total: pagination.pageSize ?? 10,
    }, qp);
    return this.appHttpService.getService(`/v1/bid/sheet/${bidSheet.id}/item`, qp).toPromise()
      .then((resp: any) => {
        if (Array.isArray(resp.data)) {
          return {
            result: resp.data.map(o => new BidItem(o)),
            meta: Object.assign(resp.meta || {}, { type: resp.type }),
          };
        }
        throw new Error('Error parsing response');
      });
  }

  /**
   * Downloads bid sheet as csv
   * @param sheet BidSheet
   * @param qp query-params
   * @returns {Promise}
   */
  downloadBidSheet(sheet: BidSheet, qp: any = {}): Promise<any> {
    return this.appHttpService.getService(`/v1/bid/sheet/${sheet.id}/csv`, qp, { responseType: 'blob' })
      .toPromise()
      .then((resp: Blob) => {
        const name = `${sheet.project?.number || sheet.project?.name} bidsheet.csv`
        return saveAs(resp, name.trim());
      });
  }

  /**
   * Fetches BidItem
   * @param id string
   * @param qp query params
   * @returns {Promise<BidItem>}
   */
  getBidItem(id: string, qp: any = {}): Promise<BidItem> {
    return this.appHttpService.getService(`/v1/bid/item/${id}`, qp)
      .toPromise()
      .then((resp: any) => new BidItem(resp.data));
  }

  /**
   * Add heading to biditem
   * @param heading Heading
   * @param biditem Biditem
   * @returns {Promise<string>} message
   */
  addHeading(heading: Heading, biditem: BidItem): Promise<string> {
    return this.appHttpService
      .putService(`/v2/heading/${heading?.id}/bid-item/${biditem.id}`, {})
      .toPromise()
      .then((resp: any) => resp?.message);
  }

  /**
   * Remove heading from biditem
   * @param heading Heading
   * @param biditem Biditem
   * @returns {Promise<string>} message
   */
  removeHeading(heading: Heading, biditem: BidItem): Promise<string> {
    return this.appHttpService
      .deleteService(`/v2/heading/${heading?.id}/bid-item/${biditem.id}`, {})
      .toPromise()
      .then((resp: any) => resp?.message);
  }

  getRollup(biditem: BidItem, headerItem: ReportBidItem, lineItems: ReportBidItem[] = []) {

    const { rollup_field: rollupField, headings, record_by_station } = biditem;
    const heading = headings.find(o => headerItem?.heading?.id === o.id || headerItem?.heading_id === o.id);
    if (!rollupField || !record_by_station || !heading || !heading.stations?.length) {
      return headerItem?.quantity || 0;
    }

    lineItems.map((li: any, i) => {
      if (li.station_id && !li?.station) {
        li.station = (heading.stations || []).find(o => o.id === li.station_id);
      }
    });

    heading.sortLineItems(lineItems); // sort lineitems as server won't give same order

    lineItems.map((li: any, i) => {
      if (i === 0) { // do not calc rollup for first li
        li.quantity = 0;
        return;
      }
      const prev = lineItems[i - 1];
      const distance = heading.getDistance(prev.station, li.station);
      const prevValue = Number.parseFloat(prev.getFieldValue(rollupField, { getDirty: true }));
      const currValue = Number.parseFloat(li.getFieldValue(rollupField, { getDirty: true }));

      if (Util.isNumeric(prevValue) && Util.isNumeric(currValue)) {
        li.quantity = distance * ((prevValue + currValue) / 2);
      }
    });
    return lineItems.map(li => li.quantity).reduce((p, c) => (p + c), 0);
  }

  updateSiteQuantity(biditem: BidItem, site: Site, qty: number): Promise<BidItem> {
    return this.appHttpService
      .putService(`/v2/bid-item/${biditem?.id}/site/${site.id}/${qty}`, {})
      .toPromise();
  }

  /**
   * Calculate total quantity from sources
   * This is the common method used by both daily report sidebar and fullscreen map popup
   * to ensure consistent quantity display
   * @param sources Array of sources with source_quantity
   * @returns Total quantity summed from all sources
   */
  calculateTotalQuantityFromSources(sources: any[]): number {
    if (!sources || !Array.isArray(sources) || sources.length === 0) {
      return 0;
    }

    return sources.reduce((sum, source) => {
      const qty = source?.source_quantity ?? 0;
      return sum + (isNaN(qty) ? 0 : Number(qty));
    }, 0);
  }

  /**
   * Calculate total quantity for a bid item
   * This is the common method used by both daily report sidebar and fullscreen map popup
   * to ensure consistent quantity calculation across the application
   *
   * Handles:
   * - Rollup formulas (dynamic calculation)
   * - Manual entry quantities
   * - Source quantities (single and multiple)
   * - Direct line item quantities
   *
   * @param bidItem BidItem
   * @param lineItems Array of line items with sources and field values
   * @returns Total quantity calculated from all line items
   */
  calculateBidItemTotalQuantity(bidItem: BidItem, lineItems: any[]): number {
    if (!lineItems || lineItems.length === 0) {
      return 0;
    }

    const lineItemTotals: { [key: string]: number } = {};

    lineItems.forEach(lineItem => {
      const lineItemId = lineItem.id;
      lineItemTotals[lineItemId] = this.calculateSingleLineItemQuantity(bidItem, lineItem);
    });

    // Sum up all line item totals
    return Object.values(lineItemTotals).reduce((sum, qty) => {
      return sum + (isNaN(qty) ? 0 : Number(qty));
    }, 0);
  }

  /**
   * Calculate quantity for a single line item
   * Uses the EXACT same logic as the daily report sidebar (multiline-bid-item-details.component.ts)
   * @param bidItem BidItem
   * @param lineItem Single line item with sources and field values
   * @returns Calculated quantity for this line item
   */
  private calculateSingleLineItemQuantity(bidItem: any, lineItem: any): number {
    // GPS geometry items: use shared GPS quantity calculation
    const gpsQty = BidService.calculateGpsTotalQuantity(lineItem, bidItem);
    if (gpsQty !== null) return gpsQty;

    const hasSources = lineItem.sources && Array.isArray(lineItem.sources) && lineItem.sources.length > 1;

    if (hasSources) {
      // Multiple sources/children - sum them up
      // This matches multiline-bid-item-details.component.ts lines 312-326
      let groupTotal = 0;
      lineItem.sources.forEach((source, idx) => {
        let qty = 0;
        if (bidItem.rollup) {
          // Use rollup formula - matches line 318
          const fieldValues = source.fieldValues || source.field_values || {};
          qty = this.calculateDynamicRollupValue(bidItem, fieldValues);
        } else {
          // Use source quantity - matches line 320
          qty = source.modified_quantity ?? source.source_quantity ?? 0;
        }
        qty = isNaN(qty) ? 0 : Number(qty);
        groupTotal += qty;
      });

      // Check if manual entry - matches lines 329-335
      if (bidItem.rollup && lineItem.rollup_quantity_manual !== true) {
        return groupTotal;
      } else if (bidItem.rollup && lineItem.rollup_quantity_manual === true) {
        return lineItem.quantity ?? 0;
      } else {
        return groupTotal;
      }
    } else {
      // Single line item - matches multiline-bid-item-details.component.ts lines 341-357
      if (bidItem.rollup) {
        if (lineItem.rollup_quantity_manual !== true) {
          // Dynamic rollup - matches lines 342-343
          const source = lineItem.sources?.[0];
          const fieldValues = source?.fieldValues || source?.field_values || lineItem.field_values || {};
          const result = this.calculateDynamicRollupValue(bidItem, fieldValues);
          return result;
        } else {
          // Manual entry - matches lines 351-352
          return lineItem.quantity ?? 0;
        }
      } else {
        // Non-rollup - matches lines 355-356
        const source = lineItem.sources?.[0];
        return source?.modified_quantity ?? source?.source_quantity ?? lineItem.quantity ?? 0;
      }
    }
  }

  /**
   * Calculate dynamic rollup value using formula
   * This is the EXACT same logic as multiline-bid-item-details.component.ts line 361-364
   * @param bidItem BidItem with rollup formula and fields
   * @param values Field values object or array
   * @returns Calculated quantity from rollup formula
   */
  private calculateDynamicRollupValue(bidItem: any, values: any): number {
    const mapObj = this.getFieldValuesMap(bidItem, values);
    return Number((Parser.evaluate(bidItem.rollup_formula, mapObj)).toFixed(bidItem?.quantity_rounding ?? 2));
  }

  /**
   * Get field values map for rollup formula evaluation
   * This is the EXACT same logic as multiline-bid-item-details.component.ts lines 374-382
   * @param bidItem BidItem with fields
   * @param values Field values (can be object with field IDs as keys or array)
   * @returns Map of field names to values
   */
  private getFieldValuesMap(bidItem: any, values: any): any {
    const mapObj = {};

    // Handle case where bidItem.fields is not available
    if (!bidItem.fields || !Array.isArray(bidItem.fields)) {
      console.warn('⚠️ bidItem.fields is missing or not an array, returning empty map');
      return mapObj;
    }

    // Use the EXACT same logic as multiline-bid-item-details.component.ts lines 374-382
    bidItem.fields.forEach(field => {
      const value = values[field.id];
      mapObj[field.name] = Number(value);
    });

    return mapObj;
  }

  // ── GPS Geometry Utilities (shared across components) ──

  // Unit conversion constants
  static readonly SQ_METERS_TO_SQ_FEET = 10.7639;
  static readonly METERS_TO_FEET = 3.28084;

  /** Check if a line item has GPS geometry data */
  static hasGpsGeometry(lineItem: any): boolean {
    return lineItem?.geometry?.type === 'FeatureCollection' && lineItem.geometry.features?.length > 0;
  }

  /**
   * Extract individual geometries from a GeoJSON geometry object.
   * Handles FeatureCollection, Feature, and raw geometry with coordinates.
   * Returns array of { geometry, gps_feature_index? } objects.
   */
  static extractGeometries(geom: any): { geometry: any; gps_feature_index?: number }[] {
    if (!geom) return [];
    if (geom.type === 'FeatureCollection' && geom.features?.length) {
      return geom.features
        .map((feature, idx) => feature.geometry ? { geometry: feature.geometry, gps_feature_index: idx } : null)
        .filter(Boolean);
    }
    if (geom.type === 'Feature' && geom.geometry) {
      return [{ geometry: geom.geometry }];
    }
    if (geom.coordinates) {
      return [{ geometry: geom }];
    }
    return [];
  }

  /**
   * Extract coordinates arrays from a GeoJSON geometry object.
   * Useful for center-point calculations.
   */
  static extractCoordinates(geom: any): any[] {
    if (!geom) return [];
    if (geom.type === 'FeatureCollection' && geom.features?.length) {
      return geom.features.filter(f => f.geometry?.coordinates).map(f => f.geometry.coordinates);
    }
    if (geom.type === 'Feature' && geom.geometry?.coordinates) {
      return [geom.geometry.coordinates];
    }
    if (geom.coordinates) {
      return [geom.coordinates];
    }
    return [];
  }

  /** Get filtered features (only those with geometry) from a line item */
  static getGpsFeatures(lineItem: any): any[] {
    if (!lineItem?.geometry?.features) return [];
    return lineItem.geometry.features.filter(f => f.geometry);
  }

  /**
   * Check if a feature has a quantity mapping from GPS: an area measurement, or the UOM quantity
   * mobile computed at save — a Point's mapped count (e.g. `source_count` = 1) or, for volume
   * captures (MultiPoint hull), the quantity / m³ volume. Without this, these features defaulted
   * to "auto" mode, which needs a rollup formula that shape-mapped items don't have, and displayed 0.
   */
  static featureHasQuantityMapping(feature: any): boolean {
    const measurements = feature?.properties?.source?.measurements;
    if (!measurements) return false;
    if (measurements.area !== undefined) return true;
    const geomType = feature?.geometry?.type;
    if (geomType === 'Point') return measurements.quantity !== undefined;
    return geomType === 'MultiPoint'
      && (measurements.quantity !== undefined || measurements.volume !== undefined);
  }

  /** Volume captured by the mobile 3D hull, in cubic meters. null when absent (no altitudes). */
  static getVolumeCubicMeters(feature: any): number | null {
    const volume = feature?.properties?.source?.measurements?.volume;
    return typeof volume === 'number' && Number.isFinite(volume) ? volume : null;
  }

  /** Get the initial GPS quantity mode for a feature (from persisted or default) */
  static getInitialGpsQuantityMode(feature: any): string {
    const persistedMode = feature?.properties?.modified_fields?.quantityMode;
    if (persistedMode) return persistedMode;
    const geomType = feature?.geometry?.type;
    if (geomType === 'LineString') return 'auto';
    return BidService.featureHasQuantityMapping(feature) ? 'measured' : 'auto';
  }

  /** Get measured quantity from GPS feature */
  static calculateGpsMeasuredQty(feature: any): number {
    const measurements = feature?.properties?.source?.measurements || {};
    return measurements.quantity || measurements.area || measurements.length || measurements.count || 0;
  }

  /** Calculate auto quantity for a GPS feature using rollup formula */
  static calculateGpsAutoQty(feature: any, bidItem: any): number {
    if (!bidItem?.rollup_formula) return 0;
    const measurements = feature?.properties?.source?.measurements || {};
    const modifiedFields = feature?.properties?.modified_fields || {};
    const geomType = feature?.geometry?.type;

    try {
      const context: any = {};
      if (geomType === 'LineString') {
        context.length_f = modifiedFields.length_f || 0;
        context.width_f = modifiedFields.width_f || 0;
        context.area = measurements.area || 0;
        context.perimeter = measurements.perimeter || 0;
        context.count = measurements.count || 0;
      } else if (geomType === 'Polygon') {
        context.length_f = modifiedFields.length_f || 0;
        context.width_f = modifiedFields.width_f || 0;
        context.area = measurements.area ? measurements.area * BidService.SQ_METERS_TO_SQ_FEET : 0;
        context.perimeter = measurements.perimeter ? measurements.perimeter * BidService.METERS_TO_FEET : 0;
        context.count = measurements.count || 0;
      } else {
        // Generic: use all modified_fields except meta keys
        Object.keys(modifiedFields).forEach(key => {
          if (key !== 'quantity' && key !== 'quantityMode') {
            context[key] = modifiedFields[key] || 0;
          }
        });
      }
      return Number(Parser.evaluate(bidItem.rollup_formula, context).toFixed(bidItem?.quantity_rounding ?? 2));
    } catch {
      return 0;
    }
  }

  /** Calculate quantity for a single GPS feature based on its mode */
  static calculateGpsFeatureQuantity(feature: any, mode: string, bidItem: any): number {
    if (mode === 'measured' || mode === 'gps') return BidService.calculateGpsMeasuredQty(feature);
    if (mode === 'auto') return BidService.calculateGpsAutoQty(feature, bidItem);
    if (mode === 'manual') return Number(feature?.properties?.modified_fields?.quantity) || 0;
    return 0;
  }

  /**
   * Calculate total GPS geometry quantity for a line item.
   * Returns null if the line item has no GPS geometry data (caller should fall through to other logic).
   */
  static calculateGpsTotalQuantity(lineItem: any, bidItem: any): number | null {
    if (!BidService.hasGpsGeometry(lineItem)) return null;

    let total = 0;
    let hasGpsQuantity = false;

    for (const feature of lineItem.geometry.features) {
      const modFields = feature?.properties?.modified_fields;
      if (!modFields || (modFields.quantity == null && !modFields.quantityMode)) continue;
      hasGpsQuantity = true;

      const mode = modFields.quantityMode;
      if (mode === 'auto' && bidItem?.rollup_formula) {
        total += BidService.calculateGpsAutoQty(feature, bidItem);
      } else {
        total += Number(modFields.quantity) || 0;
      }
    }

    return hasGpsQuantity ? total : null;
  }
}
